import { FormEvent, useState } from "react";
import Alert from "../../components/alert";
import Button from "../../components/button";
import { InputField, Select } from "../../components/input-functions";
import Modal from "../../components/modal";
import { AuthzRole } from "../../actions/authz.action";
import { CreateAdminWithUserProfileBody } from "../../dto/authz.dto";
import { isEmail } from "../../utils";

interface AddPortalUserModalProps {
  open: boolean;
  roles: AuthzRole[];
  submitting: boolean;
  submitError?: string;
  onClose: () => void;
  onSubmit: (body: CreateAdminWithUserProfileBody) => void;
}

export default function AddPortalUserModal({
  open,
  roles,
  submitting,
  submitError,
  onClose,
  onSubmit,
}: AddPortalUserModalProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [validationError, setValidationError] = useState("");

  const roleOptions = roles.map((r) => ({ value: r._id, label: r.name }));

  function reset() {
    setName("");
    setEmail("");
    setRole("");
    setValidationError("");
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setValidationError("");
    if (!name.trim()) {
      setValidationError("Name is required");
      return;
    }
    if (!isEmail(email)) {
      setValidationError("Enter a valid email address");
      return;
    }
    if (!role) {
      setValidationError("Please select a role");
      return;
    }
    onSubmit({
      name: name.trim(),
      email: email.trim().toLowerCase(),
      roles: ["ADMIN"],
      clientId: "REFYNE",
      role,
    });
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add Portal User"
      description="Create admin and portal access for the user in one step. No password is set here."
      hasSubmitButton={false}
      hasCancelButton={false}
      closeOnBackdropClick={false}
      closeOnEscPress={false}
    >
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <InputField
            id="add-portal-user-name"
            label="Full Name"
            required
            placeholder="e.g. Rohan Verma"
            value={name}
            disabled={submitting}
            onChange={(e) => setName(e.target.value)}
          />
          <InputField
            id="add-portal-user-email"
            label="Email"
            required
            placeholder="e.g. rohan.verma@refyne.co.in"
            value={email}
            disabled={submitting}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div className="form-label-group my-2">
            <label htmlFor="add-portal-user-role" style={{ color: "#495057" }}>
              Role <span className="rf-input-star">*</span>
            </label>
            <Select
              id="add-portal-user-role"
              className="form-control"
              placeholder="Select a role"
              options={roleOptions}
              value={role}
              disabled={submitting}
              onChange={(e) => setRole(e.target.value)}
            />
          </div>
        </div>
        {validationError && (
          <Alert type="danger" message={validationError} canDismiss />
        )}
        {submitError && (
          <Alert type="danger" message={submitError} canDismiss />
        )}
        <div className="d-flex justify-content-end mt-3">
          <Button
            variant="text"
            onClick={handleClose}
            type="button"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            disabled={submitting}
          >
            Add User
          </Button>
        </div>
      </form>
    </Modal>
  );
}
